import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import img from "./all_bg.jpg";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";
const TABLE_NAME = "conference_proceeding";

const ConferenceProceedings = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [formData, setFormData] = useState({
    conference_date: null,
    paper_title: "",
    conference_title: "",
    organizer_name: "",
    proceedings_link: "",
    country: "",
    scopus_indexed: "",
  });
  const [recordId, setRecordId] = useState(null);

  const fetchRecords = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/${TABLE_NAME}`, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch records");
      const data = await res.json();
      const processed = data
  .map((rec) => ({
    ...rec,
    conference_date: rec.conference_date
      ? new Date(rec.conference_date)
      : null,
  }))
  .sort((a, b) => {
    if (!a.conference_date) return 1;
    if (!b.conference_date) return -1;
    return b.conference_date - a.conference_date; // Latest year and month first
  });

setRecords(processed);
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      conference_date: formData.conference_date
        ? format(formData.conference_date, "yyyy-MM") + "-01"
        : null,
    };
    try {
      const url = recordId
        ? `${BACKEND_URL}/api/${TABLE_NAME}/${recordId}`
        : `${BACKEND_URL}/api/${TABLE_NAME}`;
      const res = await fetch(url, {
        method: recordId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to save record");

      toast.success(
        recordId ? "Record updated successfully!" : "Record added successfully!"
      );
      setFormData({
        conference_date: null,
        paper_title: "",
        conference_title: "",
        organizer_name: "",
        proceedings_link: "",
        country: "",
        scopus_indexed: "",
      });
      setRecordId(null);
      fetchRecords();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = (rec) => {
    setFormData({
      conference_date: rec.conference_date
        ? new Date(rec.conference_date)
        : null,
      paper_title: rec.paper_title || "",
      conference_title: rec.conference_title || "",
      organizer_name: rec.organizer_name || "",
      proceedings_link: rec.proceedings_link || "",
      country: rec.country || "",
      scopus_indexed: rec.scopus_indexed || "",
    });
    setRecordId(rec.id);
    toast.info("Editing record...");
  };

  const handleDelete = (rec) => {
    const toastId = toast.info(
      <div>
        Delete this record?
        <button
          onClick={async () => {
            try {
              const res = await fetch(
                `${BACKEND_URL}/api/${TABLE_NAME}/${rec.id}`,
                {
                  method: "DELETE",
                  credentials: "include",
                }
              );
              if (!res.ok) throw new Error("Failed to delete record");
              setRecords((prev) => prev.filter((r) => r.id !== rec.id));
              toast.update(toastId, {
                render: "Record deleted successfully!",
                type: "success",
                autoClose: 3000,
              });
            } catch (err) {
              toast.update(toastId, {
                render: `Delete error: ${err.message}`,
                type: "error",
                autoClose: 3000,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  const inputStyle = {
    padding: "10px",
    marginTop: "5px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    width: "100%",
    fontSize: "16px",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        fontFamily: "Arial, sans-serif",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />
      <div
        style={{
          maxWidth: "900px",
          margin: "0 auto",
          background: "#fff",
          padding: "30px 40px",
          borderRadius: "10px",
          boxShadow: "0px 0px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            color: "#1e3a8a",
            marginBottom: "25px",
          }}
        >
          Conference Proceedings / Presentations
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Conference Date (mm/yyyy){" "}
              <span
                style={{
                  fontWeight: 300,
                  fontSize: "0.85em",
                  marginLeft: "6px",
                  color: "#666",
                }}
              >
                (start with latest conference)
              </span>
            </label>
            <DatePicker
              selected={formData.conference_date}
              onChange={(date) =>
                setFormData({ ...formData, conference_date: date })
              }
              dateFormat="MM/yyyy"
              showMonthYearPicker
              placeholderText="Select month/year"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Paper Title
            </label>
            <input
              type="text"
              name="paper_title"
              value={formData.paper_title}
              onChange={handleChange}
              required
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Conference Title
            </label>
            <input
              type="text"
              name="conference_title"
              value={formData.conference_title}
              onChange={handleChange}
              required
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Organizer's Name
            </label>
            <input
              type="text"
              name="organizer_name"
              value={formData.organizer_name}
              onChange={handleChange}
              required
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Proceedings Link (if available){" "}
            </label>
            <input
              type="text"
              name="proceedings_link"
              value={formData.proceedings_link}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Country
            </label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Scopus Indexed?
            </label>
            <select
              name="scopus_indexed"
              value={formData.scopus_indexed}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="">Select...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>

          <button
            type="submit"
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              marginTop: "20px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {recordId ? "Update Record" : "Add Record"}
          </button>
        </form>

        {records.length > 0 && (
          <div style={{ marginTop: "30px", overflowX: "auto" }}>
            <h3 style={{ color: "#1e3a8a" }}>Your Conference Proceedings</h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  {[
                    "Date",
                    "Paper Title",
                    "Conference Title",
                    "Organizer",
                    "Proceedings Link",
                    "Country",
                    "Scopus Indexed",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        background: "#f1f5f9",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.conference_date
                        ? `${String(
                            rec.conference_date.getMonth() + 1
                          ).padStart(
                            2,
                            "0"
                          )}/${rec.conference_date.getFullYear()}`
                        : ""}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.paper_title}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.conference_title}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.organizer_name}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.proceedings_link}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.country}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.scopus_indexed}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "center",
                      }}
                    >
                      <button
                        onClick={() => handleEdit(rec)}
                        style={{
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          marginRight: "5px",
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(rec)}
                        style={{
                          background: "#ef4444",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "25px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#1e3a8a",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConferenceProceedings;
